import { MULTI_SELECT_FIELDS } from '../partials/field-constants';

import type { StepFormField, FieldValue, FieldValues } from '../types/workflow';

/**
 * Composable for managing field types, validation, and properties restoration
 * Helps maintain and restore field states in workflow form configurations
 */
export function useRestoreFieldTypes() {
  /**
   * Store original field configurations for restoration
   */
  const originalFieldConfigs = new Map<string, Partial<StepFormField>>();

  /**
   * Saves original field configuration for later restoration
   * @param fieldKey - The key of the field to save
   * @param field - The field configuration to save
   */
  const saveOriginalFieldConfig = (fieldKey: string, field: Partial<StepFormField>) => {
    if (!originalFieldConfigs.has(fieldKey)) {
      originalFieldConfigs.set(fieldKey, {
        type: field.type,
        validation: field.validation ? { ...field.validation } : undefined,
        show: field.show,
      });
    }
  };

  /**
   * Updates field type
   * @param field - The field to update
   * @param type - The new field type
   */
  const updateFieldType = (field: StepFormField, type: StepFormField['type']) => {
    if (field) {
      field.type = type;
    }
  };

  /**
   * Updates field visibility
   * @param field - The field to update
   * @param show - Whether to show or hide the field
   */
  const updateFieldVisibility = (field: StepFormField, show: boolean) => {
    if (field) {
      field.show = show;
    }
  };

  /**
   * Updates field validation rules
   * @param field - The field to update
   * @param validation - The validation rules to apply
   * @param merge - Whether to merge with existing validation or replace
   */
  const updateFieldValidation = (
    field: StepFormField,
    validation: Partial<StepFormField['validation']>,
    merge = true,
  ) => {
    if (field) {
      if (merge && field.validation) {
        field.validation = { ...field.validation, ...validation };
      } else {
        field.validation = validation as StepFormField['validation'];
      }
    }
  };

  /**
   * Clears specific validation rules from a field
   * @param field - The field to update
   * @param rules - Array of validation rule names to clear
   */
  const clearValidationRules = (field: StepFormField, rules: string[]) => {
    if (field && field.validation) {
      rules.forEach((rule) => {
        if (rule in field.validation!) {
          (field.validation as any)[rule] = false;
        }
      });
    }
  };

  /**
   * Restores field to its original configuration
   * @param fieldKey - The key of the field to restore
   * @param field - The field to restore
   */
  const restoreFieldConfig = (fieldKey: string, field: StepFormField) => {
    const original = originalFieldConfigs.get(fieldKey);
    if (original && field) {
      if (original.type) {
        field.type = original.type;
      }
      if (original.validation) {
        field.validation = { ...original.validation };
      }
      if (typeof original.show === 'boolean') {
        field.show = original.show;
      }
    }
  };

  /**
   * Helper to handle phone number validation
   * Sets phone validation and clears email validation
   * @param field - The field to update
   */
  const setPhoneValidation = (field: StepFormField) => {
    if (field && field.validation) {
      field.validation.phone = true;
      field.validation.email = false;
    }
  };

  /**
   * Helper to handle email validation
   * Sets email validation and clears phone validation
   * @param field - The field to update
   */
  const setEmailValidation = (field: StepFormField) => {
    if (field && field.validation) {
      field.validation.email = true;
      field.validation.phone = false;
    }
  };

  /**
   * Helper to clear both phone and email validation
   * @param field - The field to update
   */
  const clearContactValidation = (field: StepFormField) => {
    if (field && field.validation) {
      field.validation.phone = false;
      field.validation.email = false;
    }
  };

  /**
   * Batch update multiple fields
   * @param updates - Array of field update configurations
   */
  const batchUpdateFields = (
    updates: Array<{
      field: StepFormField;
      type?: StepFormField['type'];
      show?: boolean;
      validation?: Partial<StepFormField['validation']>;
    }>,
  ) => {
    updates.forEach(({ field, type, show, validation }) => {
      if (type !== undefined) {
        updateFieldType(field, type);
      }
      if (show !== undefined) {
        updateFieldVisibility(field, show);
      }
      if (validation !== undefined) {
        updateFieldValidation(field, validation);
      }
    });
  };

  /**
   * Check if a field key is a multi-select field
   * Uses the global MULTI_SELECT_FIELDS constant
   * @param key - The field key to check
   * @returns Boolean indicating if the field is a multi-select field
   */
  const isMultiSelectField = (key: string): boolean => {
    return MULTI_SELECT_FIELDS.includes(key as any);
  };

  /**
   * Conditionally updates field type based on operator value
   * Common pattern: exists/notExists = hide field, other operators = show field
   * @param field - The field to update
   * @param operator - The operator value
   * @param options - Configuration options
   */
  const updateFieldTypeByOperator = (
    field: StepFormField,
    operator: string,
    options: {
      existsOperators?: string[];
      defaultType?: StepFormField['type'];
      listType?: StepFormField['type'];
      multiSelectOperators?: string[];
      condition?: (operator: string) => boolean;
    } = {},
  ) => {
    const {
      existsOperators = ['exists', 'notExists'],
      defaultType = 'text',
      listType = 'list',
      multiSelectOperators = ['hasAnyOf', 'hasNoneOf'],
      condition,
    } = options;

    if (!field) {
      return;
    }

    if (existsOperators.includes(operator)) {
      field.show = false;
      field.type = defaultType;
    } else if (multiSelectOperators.includes(operator)) {
      field.show = true;
      if (condition && condition(operator)) {
        field.type = listType;
      } else {
        field.type = defaultType;
      }
    } else {
      field.show = true;
      field.type = defaultType;
    }
  };

  /**
   * Updates validation based on field key
   * Common pattern for contact fields (phoneNumber, emailAddress, etc.)
   * @param valuesField - The values field to update
   * @param key - The selected key (e.g., 'phoneNumber', 'emailAddress', 'full_name')
   */
  const updateValidationByKey = (valuesField: StepFormField, key: string) => {
    if (!valuesField || !valuesField.validation) {
      return;
    }

    if (key === 'phone_number') {
      setPhoneValidation(valuesField);
      valuesField.validation.textNoSpecialChars = false;
    } else if (key === 'email') {
      setEmailValidation(valuesField);
      valuesField.validation.textNoSpecialChars = false;
    } else if (key === 'full_name') {
      clearContactValidation(valuesField);
      valuesField.validation.textNoSpecialChars = true;
    } else {
      clearContactValidation(valuesField);
      valuesField.validation.textNoSpecialChars = false;
    }
  };

  /**
   * Triggers onChange handler of another field
   * Useful for cascading field updates
   * @param field - The field whose onChange to trigger
   * @param value - The current value
   * @param allValues - All form values
   * @param allFields - All form fields
   */
  const triggerFieldOnChange = (
    field: StepFormField,
    value: FieldValue,
    allValues: FieldValues,
    allFields: Record<string, StepFormField>,
  ) => {
    if (field && 'onChange' in field && field.onChange) {
      // Type assertion needed because onChange has different signatures for different field types
      (field.onChange as (value: FieldValue, allValues: FieldValues, allFields: Record<string, StepFormField>) => void)(
        value,
        allValues,
        allFields,
      );
    }
  };

  return {
    // State management
    saveOriginalFieldConfig,
    restoreFieldConfig,

    // Basic updates
    updateFieldType,
    updateFieldVisibility,
    updateFieldValidation,
    clearValidationRules,

    // Validation helpers
    setPhoneValidation,
    setEmailValidation,
    clearContactValidation,
    updateValidationByKey,

    // Batch operations
    batchUpdateFields,

    // Common patterns
    updateFieldTypeByOperator,
    triggerFieldOnChange,
    isMultiSelectField,
  };
}
